// Live personalization bar: leather luggage tags, cardholders, keychains and
// flasks engraved on site, plus a leather-patch hat bar. Targets (OpenSEO US,
// 2026-11-03): personalized luggage tags 8,100, custom luggage tags 6,600,
// leather luggage tags 5,400, leather patch hat 5,400 (the patch hat product
// page owns "custom leather patch hats"), hat bar 2,900, gift bar 1,300,
// monogramming nyc 720, patch bar 590. Investment range, capacity and COI:
// src/data/event-engraving.js. Hat bar: patches have a heat-activated
// adhesive backing; we engrave on site, then heat-press the patch onto the cap
// (owner 2026-11-03; heat-press guides from Epilog and Monterey Company).
const {
  eventPricing,
  eventCapacity,
  eventPriceAnswer,
  eventInsurance,
} = require('../event-engraving')
const { onsiteEngravingFacts: onsite } = require('../onsite-engraving')
const { images } = require('../engraving-services')

const luggageTagsHatBar = {
  key: 'luggageTagsHatBar',
  path: '/services/laser-engraving/luggage-tags-hat-bar/',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
  reviewedNote: 'Investment and capacity from PrintAWorld’s 2026 event quotes',
  title: 'Personalized Leather Luggage Tags & Live Hat Bar | NYC',
  description:
    'A live personalization bar for NYC events: custom leather luggage tags, cardholders, keychains and a leather patch hat bar, engraved and pressed on site.',
  crumbs: [
    ['Laser engraving', '/services/laser-engraving/'],
    ['On-site engraving', onsite.path],
  ],
  crumb: 'Luggage tags and hat bar',
  eyebrow: 'Live personalization bar / New York City',
  heading:
    'Custom leather luggage tags and a leather patch hat bar, personalized live at your event.',
  answer: `PrintAWorld runs a live personalization bar at New York events: guests pick a leather luggage tag, cardholder, keychain or flask and get their name or initials laser engraved while they wait, or choose a cap and a leather patch that we engrave on site and heat-press onto the hat. The brand supplies the items or we source them, from about $10–$30 per guest. One station engraves about ${eventCapacity.perHour} items an hour, and most brand activations run ${eventPricing.investment}.`,
  cta: 'Get a personalization bar quote',
  heroPhoto: {
    url: images.leather.url,
    alt: images.leather.alt,
    caption: 'Leather engraved in our studio',
  },
  keyFacts: [
    {
      label: 'Per station',
      value: `${eventCapacity.perHour} / hour`,
      note: 'About 250 in 3 hours with batch loading',
    },
    {
      label: 'Activations',
      value: '$5K–$10K+',
      note: 'Most brand events, plus tax',
    },
    { label: 'Items', value: '$10–$30 / guest', note: 'About; or use yours' },
    { label: 'Deposit', value: '50%', note: 'Reserves the date' },
  ],
  sections: [
    {
      type: 'cards',
      id: 'items',
      eyebrow: 'The menu',
      title: 'Luggage tags, cardholders, keychains, flasks and hats.',
      intro:
        'Guests choose one item and a name, initials or an icon your team approved. Laser monogramming, not embroidery: the mark is engraved into the leather or metal.',
      index: '01 / THE ITEMS',
      items: [
        {
          label: 'Travel',
          title: 'Leather luggage tags',
          body: 'The classic gift-bar item: a real or faux leather luggage tag with the guest’s name or initials.',
        },
        {
          label: 'Everyday',
          title: 'Cardholders',
          body: 'Slim leather cardholders, monogrammed on the front.',
        },
        {
          label: 'Small',
          title: 'Keychains',
          body: 'Leather or metal keychains, quick to engrave for a big guest list.',
        },
        {
          label: 'Metal',
          title: 'Stainless steel flasks',
          body: 'A name or date engraved on a stainless flask.',
        },
        {
          label: 'Hat bar',
          title: 'Leather patch hats',
          body: 'Guests pick a hat and a patch. We laser-engrave the leather patch on site with their name or your logo, then heat-press it onto the hat.',
          link: [
            'Leather patch hats in bulk',
            '/services/laser-engraving/leather-patch-hats/',
          ],
        },
        {
          label: 'Your item',
          title: 'Brand-supplied items',
          body: 'Have your own merch? We test it first. We engrave items supplied by the brand or by us, never guests’ personal belongings.',
        },
      ],
    },
    {
      type: 'steps',
      id: 'hat-bar',
      eyebrow: 'The hat bar',
      title: 'How a live leather patch hat bar works.',
      intro:
        'The patches are real or faux leather with a heat-activated adhesive backing. We engrave them on site with a portable, enclosed laser, then bond each one to the hat with a hat heat press, in under a minute per hat.',
      index: '02 / THE HAT BAR',
      items: [
        {
          title: 'Pick a hat and a patch',
          body: 'Guests choose a cap style and color and a patch shape: a circle, a rectangle or your logo’s outline.',
        },
        {
          title: 'Type a name',
          body: 'They scan the QR code and type a name or initials, or pick your logo, with a live preview.',
        },
        {
          title: 'We engrave the patch',
          body: 'The patch is laser engraved on site from the guest queue.',
        },
        {
          title: 'We heat-press it on',
          body: 'We place the patch on the hat and press it with a hat heat press so the adhesive backing bonds. The guest leaves wearing it.',
        },
      ],
    },
    {
      type: 'steps',
      id: 'how-it-works',
      eyebrow: 'Guest flow',
      title: 'How the personalization bar works.',
      intro: eventCapacity.summary,
      index: '03 / THE GUEST FLOW',
      items: [
        {
          title: 'Pick an item',
          body: 'Guests choose a luggage tag, cardholder, keychain, flask or cap from the bar.',
        },
        {
          title: 'Scan and type',
          body: 'They scan the QR code, type a name or initials and see a live preview in your approved fonts.',
        },
        {
          title: 'We engrave',
          body: 'We engrave from one queue, in batches when the bar is busy. Hat patches are engraved, then heat pressed onto the hat.',
        },
        {
          title: 'Pick up',
          body: 'A claim number on their phone turns to ready, and a board at the bar lists finished numbers.',
        },
      ],
    },
    {
      type: 'pricing',
      id: 'pricing',
      eyebrow: 'Pricing',
      title: 'What a personalization bar costs.',
      index: '04 / THE PRICE',
      lead: {
        label: 'Most brand activations',
        value: eventPricing.investment,
        note: eventPricing.investmentNote,
      },
      rowsTitle: 'The standard activation',
      rows: eventPricing.rows,
      notes: eventPricing.notes,
      cta: 'Price my event',
    },
    {
      type: 'contrast',
      id: 'events',
      eyebrow: 'Where a bar fits',
      title: 'A gift bar for launches, conferences and client events.',
      paragraphs: [
        'A personalization bar suits store openings, travel and fashion launches, sponsor booths, client appreciation nights and holiday parties: guests leave with something made for them.',
        'For agencies, we work from your brief: the items, approved fonts and icons, the bar layout and the event hours.',
      ],
      cta: 'Plan a personalization bar',
    },
  ],
  reviews: 'laser-engraving',
  faqTitle: 'Personalization bar questions, answered.',
  faqs: [
    {
      question: 'How much does a live luggage tag or hat bar cost?',
      answer: `${eventPriceAnswer} Items we supply cost ${eventPricing.items.charAt(0).toLowerCase()}${eventPricing.items.slice(1)}.`,
    },
    {
      question: 'Can you supply the luggage tags and other items?',
      answer:
        'Yes. We can source leather luggage tags, cardholders, keychains and stainless flasks, or engrave items your brand supplies. Send spares of brand-supplied items so we can test first.',
    },
    {
      question: 'How does the leather patch hat bar work?',
      answer:
        'Guests choose a hat and a patch shape and type their name or pick your logo. We laser-engrave the leather or faux-leather patch on site, then heat-press it onto the hat; the patch has a heat-activated adhesive backing. Hats and patches are quoted with the event.',
    },
    {
      question: 'How many guests can a personalization bar serve?',
      answer: `${eventCapacity.summary} A second station shares the same queue.`,
    },
    {
      question: 'Is this embroidery or hot-foil monogramming?',
      answer:
        'Neither. We laser engrave names and initials into leather and metal. We don’t do embroidery, embossing or hot foil.',
    },
    {
      question: 'Can guests bring their own items to engrave?',
      answer:
        'No. We engrave items supplied by the brand or by us, which we’ve tested. We don’t engrave guests’ personal belongings at events.',
    },
    eventInsurance,
    {
      question: 'Do you only work in New York?',
      answer:
        'Live events are priced for New York City. For anywhere else, tell us the city and dates and we’ll say whether we can staff it.',
    },
  ],
  quote: {
    eyebrow: 'Let’s plan your bar',
    title: 'Tell us about your event.',
    body: 'Send the date, venue, hours, guest count and the items you have in mind. We’ll reply with item options and a written quote.',
    checklist: [
      'Event date, hours and venue',
      'Expected guest count',
      'Items: yours (plus spares) or sourced by us',
      'Fonts, icons or logo guests can choose',
    ],
  },
  eventPlanner: true,
  related: [
    ['On-site laser engraving', onsite.path],
    ['Live beauty engraving', '/services/laser-engraving/beauty-events/'],
    ['Leather patch hats', '/services/laser-engraving/leather-patch-hats/'],
    ['Corporate gifts', '/services/laser-engraving/corporate-gifts/'],
  ],
  schema: {
    name: 'Live personalization bar: luggage tags and hat bar',
    alternateName: [
      'Custom leather luggage tags',
      'Personalized luggage tags',
      'Hat bar',
      'Leather patch hat bar',
      'Gift bar',
      'Monogramming bar',
    ],
    serviceType: 'On-site laser engraving',
    areaServed: { '@type': 'City', name: 'New York City' },
  },
}

module.exports = { luggageTagsHatBar }
